/** Stamina-gated sprint burst for enemy chase movement. Drains while sprinting, recharges while not. */

export interface SprintConfig {
  /** Speed multiplier applied on top of chaseSpeed (e.g. 1.5 = 50% faster). */
  multiplier: number;
  /** Maximum sprint stamina pool. */
  stamina: number;
  /** Stamina drained per second while sprinting. */
  drainPerSec: number;
  /** Stamina recharged per second while not sprinting. */
  rechargePerSec: number;
  /** Speed multiplier when stamina hits 0 (e.g. 0.8 = 20% slower than chaseSpeed). */
  exhaustedPenalty: number;
  /** Seconds after stamina depletion before recharge begins. */
  exhaustedCooldown: number;
}

/** Default config for archetypes that don't specify sprint. */
export const NO_SPRINT: SprintConfig = {
  multiplier: 1.0,
  stamina: 0,
  drainPerSec: 0,
  rechargePerSec: 0,
  exhaustedPenalty: 1.0,
  exhaustedCooldown: 0,
};

/**
 * Manages sprint stamina: drains during LOS chase, recharges otherwise.
 * When stamina is depleted, applies an exhaustion speed penalty until recharged.
 */
export class SprintSystem {
  private _stamina: number;
  private readonly config: SprintConfig;
  private readonly _enabled: boolean;
  private _exhaustedTimer: number = 0;

  constructor(config: SprintConfig) {
    this.config = config;
    this._stamina = config.stamina;
    this._enabled = config.stamina > 0;
  }

  get stamina(): number { return this._stamina; }
  get maxStamina(): number { return this.config.stamina; }
  get enabled(): boolean { return this._enabled; }
  get isExhausted(): boolean { return this._enabled && this._stamina <= 0; }
  get staminaRatio(): number { return this._enabled ? this._stamina / this.config.stamina : 1; }

  /**
   * Tick the sprint system. Call every frame.
   * @param delta Frame time in ms
   * @param wantsSprint True when the enemy is in chase with LOS (should sprint)
   * @returns The speed multiplier to apply to chase speed this frame
   */
  update(delta: number, wantsSprint: boolean): number {
    if (!this._enabled) return 1.0;

    const dt = delta / 1000;

    if (wantsSprint && this._stamina > 0) {
      // Sprinting — drain stamina
      this._stamina = Math.max(0, this._stamina - this.config.drainPerSec * dt);
      if (this._stamina <= 0) {
        this._exhaustedTimer = this.config.exhaustedCooldown * 1000;
        return this.config.exhaustedPenalty;  // Exhausted THIS frame
      }
      return this.config.multiplier;
    }

    // Not sprinting — recharge
    if (this._exhaustedTimer > 0) {
      this._exhaustedTimer -= delta;
      return this.config.exhaustedPenalty;
    }

    if (this._stamina < this.config.stamina) {
      this._stamina = Math.min(this.config.stamina, this._stamina + this.config.rechargePerSec * dt);
    }

    // Exhausted with no cooldown remaining but still empty — penalty until some recharge
    if (this._stamina <= 0) {
      return this.config.exhaustedPenalty;
    }

    return 1.0;
  }

  /** Reset sprint stamina to full (used on state reset). */
  reset(): void {
    this._stamina = this.config.stamina;
    this._exhaustedTimer = 0;
  }
}
